import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

import type {
  BdVirtualClinicGender,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_VIRTUAL_CLINIC_GENDER_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

export const BD_GENDER_CONDITION_ID = 'gender';
export const FilterConditionGenderPluginType: FilterConditionPluginSpecification<
  BdVirtualClinicGender[],
  FilterConditionGenderDto,
  BdGenderCondition
> = {
  type: 'gender',
  category: 'case',
  displayName: { de: 'Geschlecht' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionGenderDto,
  ): FilterConditionPluginImpl<BdVirtualClinicGender[]> {
    return new FilterConditionGenderPlugin(obj.gender);
  },
  fromCondition(
    jsonObj: BdGenderCondition,
  ): FilterConditionPluginImpl<BdVirtualClinicGender[]> {
    return new FilterConditionGenderPlugin(
      (jsonObj.includes ?? []) as BdVirtualClinicGender[],
    );
  },
  newInstance(): FilterConditionPluginImpl<BdVirtualClinicGender[]> {
    return new FilterConditionGenderPlugin([]);
  },
};

export class FilterConditionGenderPlugin extends FilterConditionPluginImpl<
  BdVirtualClinicGender[],
  FilterConditionGenderDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    BdVirtualClinicGender[],
    FilterConditionGenderDto,
    BdGenderCondition
  > = FilterConditionGenderPluginType;

  constructor(private gender: BdVirtualClinicGender[]) {
    super(FilterConditionGenderPluginType.type);
  }

  clone(): FilterConditionGenderPlugin {
    return new FilterConditionGenderPlugin([...this.gender]);
  }

  override equals(other: FilterConditionGenderPlugin): boolean {
    return super.equals(other) && BdArrayUtil.equals(this.gender, other.gender);
  }

  override validationToText(): string {
    if (this.gender.length === 0) {
      return 'Mindestens ein Geschlecht auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdGenderCondition(this.gender, []);
  }

  toJSON(): FilterConditionGenderDto | null {
    return {
      type: this.type,
      gender: this.gender,
    };
  }

  toText(): string | null {
    return this.gender.length === 1
      ? 'ein Geschlecht'
      : `${this.gender.length} Geschlechter`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.gender.map(
      (g) => BD_VIRTUAL_CLINIC_GENDER_DISPLAY_NAMES[g] ?? g,
    );
    return { title, items };
  }

  toValue(): BdVirtualClinicGender[] {
    return this.gender;
  }
}

export class BdGenderCondition extends BdContentCondition<BdVirtualClinicGender> {
  constructor(
    includes: BdVirtualClinicGender[],
    excludes: BdVirtualClinicGender[],
  ) {
    super(BD_GENDER_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionGenderDto {
  type: string;
  gender: BdVirtualClinicGender[];
}
